// client/src/pages/Coordinator/TimeTableManagement.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./BatchManagement.css"; 
import { useAuth } from "../../contexts/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { 
  Search, Download, Plus, ChevronDown, Edit, Trash2, 
  Filter, Layout, CheckCircle, ExternalLink, FileText, X, Calendar, Clock,
  AlertTriangle, Info, GitBranch
} from "lucide-react";
import Logo from "../../assets/RCF-PP.jpg";

const BACKEND = process.env.REACT_APP_BACKEND_API_URL;
const API_BASE = `${BACKEND}/api/coordinator`;
const initial = { cohort: "", batch: "" };
const DAY_ORDER = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

export default function TimeTableManagement() {
  const [form, setForm] = useState(initial);
  const [cohorts, setCohorts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [timetable, setTimetable] = useState([]);
  
  // Restored Version State (Selection Only)
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState("");

  const [editing, setEditing] = useState(null);
  const [conflictsMap, setConflictsMap] = useState({});
  const [slotForm, setSlotForm] = useState({
    classroom_id: "", day_of_week: "MONDAY", start_time: "09:00", end_time: "10:00", class_link: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictDetails, setConflictDetails] = useState([]);

  const auth = useAuth();
  const token = auth?.user?.token;

  const axiosConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const t12 = (timeStr) => {
    if (!timeStr) return "-";
    const [hours, minutes] = timeStr.split(":");
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${minutes} ${ampm}`;
  };

  /* ------------------------- FETCH LOGIC -------------------------*/
  const fetchVersions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/timetable/versions`, axiosConfig());
      setVersions(data || []);
      if (data && data.length > 0 && !activeVersion) {
        setActiveVersion(data[0].version_id);
      }
    } catch (err) { console.error("Failed to fetch versions", err); }
  }, [axiosConfig, activeVersion]);

  useEffect(() => {
    if (token) fetchVersions();
  }, [token, fetchVersions]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/cohorts`, axiosConfig()).then(({ data }) => setCohorts(data || [])).catch(console.error);
  }, [token, axiosConfig]);

  useEffect(() => {
    if (!token || !form.cohort) {
      setFilteredBatches([]); setBatches([]); return;
    }
    axios.get(`${API_BASE}/batches?cohort_number=${form.cohort}`, axiosConfig()).then(({ data }) => {
      setBatches(data || []); setFilteredBatches(data || []);
    }).catch(console.error);
  }, [token, form.cohort, axiosConfig]);

  useEffect(() => {
    if (!token || !form.batch) {
      setClassrooms([]); setTimetable([]); return;
    }
    axios.get(`${API_BASE}/classrooms/${form.batch}`, axiosConfig()).then(({ data }) => setClassrooms(data || [])).catch(() => setClassrooms([]));
    
    // Fetch timetable for specific batch AND selected version
    fetchTimetable(form.batch, activeVersion);
  }, [token, form.batch, activeVersion, axiosConfig]);

  const fetchTimetable = async (batchId, vId) => {
    if (!batchId) return;
    setLoading(true);
    try {
      const url = `${API_BASE}/timetable?batchId=${batchId}` + (vId ? `&versionId=${vId}` : "");
      const { data } = await axios.get(url, axiosConfig());
      setTimetable(data || []);
      setConflictsMap({});
    } catch (err) { setTimetable([]); } finally { setLoading(false); }
  };

  /* ------------------------- ACTIONS -------------------------*/
  const handleSlotChange = (e) => {
    const { name, value } = e.target;
    if (name === "classroom_id") {
      const selected = classrooms.find(c => String(c.classroom_id) === String(value));
      setSlotForm(prev => ({ 
        ...prev, 
        classroom_id: value, 
        class_link: selected?.class_link || "" 
      }));
    } else {
      setSlotForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditClick = (row) => {
    setEditing(row);
    setSlotForm({
      classroom_id: row.classroom_id,
      day_of_week: row.day_of_week,
      start_time: row.start_time,
      end_time: row.end_time,
      class_link: row.class_link || "" 
    });
    setShowSlotForm(true);
  };

  const validateSlot = async () => {
    const { classroom_id, day_of_week, start_time, end_time } = slotForm;
    const params = new URLSearchParams({ 
      classroomId: classroom_id, 
      day: day_of_week, 
      startTime: start_time, 
      endTime: end_time,
      versionId: activeVersion 
    });
    if (editing) params.append("excludeId", editing.timetable_id);

    try {
      const res = await axios.get(`${API_BASE}/timetable/check-conflict?${params.toString()}`, axiosConfig());
      if (res.data?.overlap) {
        const conflicts = res.data.conflicts || [];
        setConflictsMap(conflicts.reduce((a, c) => ({ ...a, [c.timetable_id]: true }), {}));
        setConflictDetails(conflicts);
        setShowConflictModal(true);
        return { ok: false };
      }
      return { ok: true };
    } catch (err) { return { ok: false }; }
  };

  const handleCreateOrUpdate = async () => {
    if (!activeVersion) return alert("Please select a Timetable Version first.");

    const val = await validateSlot();
    if (!val.ok) return;

    const payload = {
      batch_id: form.batch,
      classroom_id: slotForm.classroom_id,
      day: slotForm.day_of_week,
      start_time: slotForm.start_time,
      end_time: slotForm.end_time,
      class_link: slotForm.class_link || null,
      version_id: activeVersion 
    };

    try {
      if (editing) {
        await axios.put(`${API_BASE}/timetable/${editing.timetable_id}`, payload, axiosConfig());
        alert("Updated Successfully");
      } else {
        await axios.post(`${API_BASE}/timetable`, payload, axiosConfig());
        alert("Created Successfully");
      }
      fetchTimetable(form.batch, activeVersion);
      setShowSlotForm(false);
      resetSlotForm();
    } catch (err) { alert("Error saving slot. Make sure all required fields are filled."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      await axios.delete(`${API_BASE}/timetable/${id}`, axiosConfig());
      fetchTimetable(form.batch, activeVersion);
    } catch (err) { alert("Delete failed"); }
  };

  const resetSlotForm = () => {
    setSlotForm({ classroom_id: "", day_of_week: "MONDAY", start_time: "09:00", end_time: "10:00", class_link: "" });
    setEditing(null);
    setConflictsMap({});
  };

  /* ------------------------- EXPORT LOGIC -------------------------*/
  const downloadPDF = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;

    try {
      const logoWidth = 50;
      const logoHeight = 50;
      doc.addImage(Logo, 'JPEG', pageWidth - margin - logoWidth, 20, logoWidth, logoHeight);
    } catch (e) {}

    const batchObj = filteredBatches.find(b => String(b.batch_id) === String(form.batch));
    const versionObj = versions.find(v => String(v.version_id) === String(activeVersion));
    const title = `COHORT ${form.cohort} - ${batchObj?.batch_name || "N/A"}`;
    const subTitle = `TIMETABLE: ${versionObj?.version_name || "Active Version"}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, pageWidth / 2, 45, { align: "center" });
    doc.setFontSize(11);
    doc.text(subTitle, pageWidth / 2, 60, { align: "center" });

    const flatRows = DAY_ORDER.filter(d => groupedTimetable[d]).flatMap(day => {
      const sessions = groupedTimetable[day];
      return sessions.map((r, index) => ({
        ...r,
        rowSpan: index === 0 ? sessions.length : 0 
      }));
    });

    autoTable(doc, {
      startY: 80,
      head: [["Day", "Time", "Subject Code", "Teacher", "Classroom", "Link"]],
      body: flatRows.map(r => [
        r.rowSpan > 0 ? { content: r.day_of_week, rowSpan: r.rowSpan } : null,
        `${t12(r.start_time)} - ${t12(r.end_time)}`,
        r.subject_code || "-",
        r.teacher_name || "-",
        r.classroom_name || "-",
        r.class_link ? "JOIN CLASS" : "-"
      ].filter(cell => cell !== null)),
      
      theme: "grid",
      headStyles: { fillColor: [16, 185, 129], halign: 'center' },
      styles: { fontSize: 8, valign: 'middle' },
      columnStyles: {
        0: { halign: 'center', fontStyle: 'bold' },
        5: { halign: 'center' }                    
      },

      willDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const link = flatRows[data.row.index]?.class_link;
          if (link && link !== "-") { doc.setTextColor(0, 0, 255); }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const link = flatRows[data.row.index]?.class_link;
          if (link && link !== "-") {
            const text = data.cell.text[0];
            const textWidth = doc.getTextWidth(text);
            const startX = data.cell.x + (data.cell.width - textWidth) / 2;
            const startY = data.cell.y + data.cell.height - 5;
            doc.setDrawColor(0, 0, 255);
            doc.line(startX, startY, startX + textWidth, startY);
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: link });
          }
        }
      }
    });

    doc.save("Timetable.pdf");
    setExportOpen(false);
  };

  const downloadExcel = () => {
    const rows = timetable.map(r => ({
      Day: r.day_of_week, Time: `${t12(r.start_time)} - ${t12(r.end_time)}`,
      "Subject Code": r.subject_code || "-", Teacher: r.teacher_name || "-",
      Classroom: r.classroom_name || "-", Link: r.class_link || "-"
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timetable");
    XLSX.writeFile(wb, "Timetable.xlsx");
    setExportOpen(false);
  };

  /* ------------------------- RENDER LOGIC -------------------------*/
  const filteredTimetable = timetable.filter(r =>
    r.subject_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.teacher_name?.toLowerCase().includes(search.toLowerCase())
  );

  const groupedTimetable = filteredTimetable.reduce((acc, curr) => {
    if (!acc[curr.day_of_week]) acc[curr.day_of_week] = [];
    acc[curr.day_of_week].push(curr);
    return acc;
  }, {});

  const orderedDays = DAY_ORDER.filter(d => groupedTimetable[d]);

  return (
    <div className="full-page-container">
      {/* HEADER */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img src={Logo} alt="Logo" style={{ width: '60px', height: '60px', borderRadius: '8px' }} />
          <div>
            <h1 className="title">Time Table Management</h1>
            <p className="subtitle">Managing scheduling blocks and class links</p>
          </div>
        </div>

        <div className="export-menu-wrapper">
          <button className="btn primary" onClick={() => setExportOpen(!exportOpen)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Export <ChevronDown size={14} />
          </button>
          {exportOpen && (
            <div className="export-dropdown shadow-lg">
              <button onClick={downloadExcel}><FileText size={14} className="mr-2 inline"/> Excel (.xlsx)</button>
              <button onClick={downloadPDF}><FileText size={14} className="mr-2 inline"/> PDF (.pdf)</button>
            </div>
          )}
        </div>
      </div>

      {/* FILTER BAR - STRUCTURED GRID LAYOUT */}
      <div className="filters-grid">
        <div className="filter-wrapper">
          <label>Version</label>
          <div className="filter-input-container">
            <GitBranch size={18} style={{ color: '#64748b' }} />
            <select value={activeVersion} onChange={e => setActiveVersion(e.target.value)}>
              {versions.length === 0 && <option value="">No Versions Found</option>}
              {versions.map(v => (
                <option key={v.version_id} value={v.version_id}>
                  {v.version_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-wrapper">
          <label>Cohort Filter</label>
          <div className="filter-input-container">
            <Filter size={18} style={{ color: '#64748b' }} />
            <select value={form.cohort} onChange={e => setForm({ cohort: e.target.value, batch: "" })}>
              <option value="">All Cohorts</option>
              {cohorts.map(c => <option key={c.cohort_number} value={c.cohort_number}>{c.cohort_name}</option>)}
            </select>
          </div>
        </div>

        <div className="filter-wrapper">
          <label>Batch Filter</label>
          <div className="filter-input-container">
            <Layout size={18} style={{ color: '#64748b' }} />
            <select value={form.batch} disabled={!form.cohort} onChange={e => setForm({ ...form, batch: e.target.value })}>
              <option value="">Select Batch</option>
              {filteredBatches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
            </select>
          </div>
        </div>

        <div className="filter-wrapper">
          <label>Search Classes</label>
          <div className="filter-input-container">
            <Search size={18} style={{ color: '#64748b' }} />
            <input placeholder="Search Teacher or Subject..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        
        {form.batch && activeVersion && (
          <div className="filter-wrapper">
            <label>&nbsp;</label>
            <button className="btn primary" style={{ height: '42px', display: 'flex', justifyContent: 'center' }} onClick={() => { resetSlotForm(); setShowSlotForm(true); }}>
              <Plus size={18} /> Create Slot
            </button>
          </div>
        )}
      </div>

      {/* DATA TABLE */}
      <div className="table-wrapper shadow-md rounded-lg bg-white">
        {!activeVersion ? (
          <div style={{ padding: '80px', textAlign: 'center' }}>
            <GitBranch size={48} style={{ color: '#d1d5db', margin: '0 auto 10px auto' }} />
            <p style={{ color: '#6b7280' }}>Please select a Timetable Version from the dropdown above to view slots.</p>
          </div>
        ) : form.batch ? (
          <table className="students-table">
            <thead>
              <tr>
                <th style={{ width: "120px" }}>Day</th>
                <th>Time Slot</th>
                <th>Subject Name</th>
                <th>Assigned Teacher</th>
                <th>Classroom</th>
                <th>Online Link</th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orderedDays.length > 0 ? orderedDays.map(day => groupedTimetable[day].map((r, idx) => (
                <tr key={r.timetable_id} style={{ backgroundColor: conflictsMap[r.timetable_id] ? "#fff1f2" : "inherit" }}>
                  <td style={{ fontWeight: '700', color: '#1e40af' }}>{idx === 0 ? day : ""}</td>
                  <td style={{ fontWeight: '600', color: '#4b5563' }}>{t12(r.start_time)} - {t12(r.end_time)}</td>
                  <td>{r.subject_name}</td>
                  <td>{r.teacher_name}</td>
                  <td><span className="badge-room">{r.classroom_name}</span></td>
                  <td>
                    {r.class_link ? (
                      <a href={r.class_link} target="_blank" rel="noreferrer" className="link-text">
                        <ExternalLink size={14} className="inline mr-1" /> Join
                      </a>
                    ) : <span style={{color: '#9ca3af'}}>-</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button onClick={() => handleEditClick(r)} className="action-btn edit"><Edit size={16}/></button>
                      <button onClick={() => handleDelete(r.timetable_id)} className="action-btn delete"><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              ))) : (
                <tr><td colSpan="7" style={{textAlign:'center', padding:'40px', color:'#94a3b8'}}>No schedule slots found for this version.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '100px', textAlign: 'center' }}>
            <Calendar size={48} style={{ color: '#d1d5db', margin: '0 auto 10px auto' }} />
            <p style={{ color: '#6b7280' }}>Select Cohort & Batch to view Timetable</p>
          </div>
        )}
      </div>

      {/* SLOT FORM MODAL */}
      {showSlotForm && (
        <div className="modal">
          <div className="modal-content medium">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="modal-title">{editing ? "Update Schedule Slot" : "Create New Schedule Slot"}</h3>
              <button onClick={() => setShowSlotForm(false)} className="close-btn"><X /></button>
            </div>
            
            <div className="edit-form grid-2-col">
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Classroom</label>
                {editing ? (
                  <div className="form-input bg-gray-100">{editing.classroom_name}</div>
                ) : (
                  <select name="classroom_id" value={slotForm.classroom_id} onChange={handleSlotChange} className="form-input">
                    <option value="">Select Classroom</option>
                    {classrooms.map(c => <option key={c.classroom_id} value={c.classroom_id}>{c.classroom_name}</option>)}
                  </select>
                )}
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Class Link</label>
                <input type="url" value={slotForm.class_link || ""} onChange={(e) => setSlotForm({...slotForm, class_link: e.target.value})} className="form-input" readOnly={!editing} />
              </div>
              <div className="form-group">
                <label>Day of Week</label>
                <select name="day_of_week" value={slotForm.day_of_week} onChange={handleSlotChange} className="form-input">
                  {DAY_ORDER.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Start Time</label><input type="time" name="start_time" value={slotForm.start_time} onChange={handleSlotChange} className="form-input" /></div>
              <div className="form-group"><label>End Time</label><input type="time" name="end_time" value={slotForm.end_time} onChange={handleSlotChange} className="form-input" /></div>
            </div>

            <div className="modal-actions" style={{ marginTop: '25px' }}>
              <button className="btn secondary" onClick={() => setShowSlotForm(false)}>Discard</button>
              <button className="btn primary" onClick={handleCreateOrUpdate}><CheckCircle size={18} className="mr-2 inline" /> Save Slot</button>
            </div>
          </div>
        </div>
      )}

      {/* CONFLICT MODAL */}
      {showConflictModal && (
        <div className="modal">
          <div className="modal-content small" style={{ borderTop: '5px solid #ef4444' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <AlertTriangle size={48} color="#ef4444" style={{ margin: '0 auto 10px auto' }} />
              <h3 className="modal-title" style={{ color: '#b91c1c' }}>Schedule Conflict Detected</h3>
              <p style={{ fontSize: '13px', color: '#6b7280' }}>The proposed time slot overlaps with existing classes in this version:</p>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px solid #fee2e2' }}>
              {conflictDetails.map((c, i) => (
                <div key={i} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: i !== conflictDetails.length - 1 ? '1px solid #fecaca' : 'none' }}>
                   <div style={{ fontWeight: '700', fontSize: '14px', color: '#991b1b' }}>{i + 1}. {c.subject_name}</div>
                   <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Info size={12}/> Teacher: {c.teacher_name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Layout size={12}/> Room: {c.classroom_name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={12}/> {t12(c.start_time)} - {t12(c.end_time)}</div>
                   </div>
                </div>
              ))}
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn primary" style={{ backgroundColor: '#ef4444', width: '100%' }} onClick={() => setShowConflictModal(false)}>
                I Understand, Let me fix it
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .full-page-container { width: 100%; padding: 25px; background-color: #f9fafb; min-height: 100vh; }
        
        /* 🔥 FIX FOR JUMBLED FILTERS */
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
          align-items: end;
        }
        .filter-wrapper {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .filter-wrapper label {
          font-size: 13px;
          font-weight: 600;
          color: #475569;
        }
        .filter-input-container {
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 0 12px;
          height: 42px;
          background: #fff;
          transition: border-color 0.2s;
        }
        .filter-input-container:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }
        .filter-input-container select, .filter-input-container input {
          border: none;
          outline: none;
          width: 100%;
          background: transparent;
          font-size: 14px;
          color: #1e293b;
        }

        .badge-room { background: #eff6ff; color: #1e40af; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; border: 1px solid #dbeafe; }
        .link-text { color: #10b981; font-weight: 600; text-decoration: none; display: flex; align-items: center; gap: 4px; }
        .action-btn { background: none; border: none; cursor: pointer; transition: transform 0.2s; }
        .action-btn.edit { color: #d97706; }
        .action-btn.delete { color: #dc2626; }
        .modal-content.medium { max-width: 600px; }
        .modal-content.small { max-width: 400px; }
      `}</style>
    </div>
  );
}